import React, { useState, useEffect } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { leaderboardService } from '../../../services/supabaseService';

const LeaderboardTab = ({ leaderboard, tournament }) => {
  const [categoryBreakdowns, setCategoryBreakdowns] = useState({});
  const [loadingBreakdowns, setLoadingBreakdowns] = useState(true);

  const predictionSettings = tournament?.predictionSettings || {};
  const matchSettings = predictionSettings?.matchLevel || {};
  const bonusSettings = predictionSettings?.bonusPoints || {};
  const tournamentSettings = predictionSettings?.tournamentLevel || {};
  const confidenceBonusEnabled = bonusSettings?.confidenceBonus?.enabled;
  const earlyPredictionEnabled = bonusSettings?.earlyPrediction?.enabled;

  const getEnabledCategories = () => {
    const cats = [];
    if (matchSettings?.matchWinner?.enabled) cats.push({ key: 'matchWinner', label: 'Match Winner' });
    if (matchSettings?.manOfMatch?.enabled) cats.push({ key: 'manOfMatch', label: 'MoM' });
    if (matchSettings?.tossWinner?.enabled) cats.push({ key: 'tossWinner', label: 'Toss' });
    if (matchSettings?.battingFirst?.enabled) cats.push({ key: 'battingFirst', label: 'Batting First' });
    if (matchSettings?.highestRunScorer?.enabled) cats.push({ key: 'highestRunScorer', label: 'Run Scorer' });
    if (matchSettings?.highestWicketTaker?.enabled) cats.push({ key: 'highestWicketTaker', label: 'Wickets' });
    return cats;
  };

  const getTournamentResultCategories = () => {
    const cats = [];
    if (tournamentSettings?.championPrediction?.enabled) cats.push({ key: 'champion', label: 'Champion' });
    if (tournamentSettings?.finalistTeams?.enabled) cats.push({ key: 'finalists', label: 'Finalists' });
    if (tournamentSettings?.top4Teams?.enabled) cats.push({ key: 'top4', label: 'Top 4' });
    if (tournamentSettings?.superOverTeam?.enabled) cats.push({ key: 'superOver', label: 'Super Over' });
    if (tournamentSettings?.playerOfTournament?.enabled) cats.push({ key: 'playerOfTournament', label: 'Player of Tournament' });
    return cats;
  };

  const enabledCategories = getEnabledCategories();
  const tournamentResultCategories = getTournamentResultCategories();

  useEffect(() => {
    const fetchAllBreakdowns = async () => {
      if (!tournament?.id || !leaderboard?.length) {
        setLoadingBreakdowns(false);
        return;
      }
      setLoadingBreakdowns(true);
      try {
        const results = await Promise.all(
          leaderboard.map(async (user) => {
            try {
              const breakdown = await leaderboardService?.getCategoryPointsBreakdown(
                tournament.id,
                user.id,
                predictionSettings
              );
              return { userId: user.id, breakdown };
            } catch {
              return { userId: user.id, breakdown: null };
            }
          })
        );
        const map = {};
        results.forEach(({ userId, breakdown }) => { map[userId] = breakdown; });
        setCategoryBreakdowns(map);
      } catch (err) {
        console.error('Error fetching category breakdowns:', err);
      } finally {
        setLoadingBreakdowns(false);
      }
    };

    fetchAllBreakdowns();
  }, [tournament?.id, leaderboard?.length]);

  const getRankIcon = (rank) => {
    if (rank === 1) return { name: 'Trophy', color: '#FFD700' };
    if (rank === 2) return { name: 'Medal', color: '#C0C0C0' };
    if (rank === 3) return { name: 'Award', color: '#CD7F32' };
    return null;
  };

  const hasAnyCategories = enabledCategories.length > 0 || tournamentResultCategories.length > 0 || confidenceBonusEnabled || earlyPredictionEnabled;

  return (
    <div className="space-y-4">
      <div className="bg-card rounded-lg shadow-elevation-sm p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg md:text-xl font-bold text-foreground flex items-center gap-2">
            <Icon name="TrendingUp" size={22} color="var(--color-primary)" />
            Tournament Rankings
          </h3>
          <Button variant="outline" size="sm" iconName="Download" iconPosition="left">
            Export
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-2 text-xs md:text-sm font-semibold text-muted-foreground">Rank</th>
                <th className="text-left py-3 px-2 text-xs md:text-sm font-semibold text-muted-foreground">Player</th>
                <th className="text-right py-3 px-2 text-xs md:text-sm font-semibold text-muted-foreground">Points</th>
                <th className="text-right py-3 px-2 text-xs md:text-sm font-semibold text-muted-foreground hidden md:table-cell">Predictions</th>
                <th className="text-right py-3 px-2 text-xs md:text-sm font-semibold text-muted-foreground hidden lg:table-cell">Accuracy</th>
              </tr>
            </thead>
            <tbody>
              {leaderboard?.map((user) => {
                const rankIcon = getRankIcon(user?.rank);
                const breakdown = categoryBreakdowns?.[user?.id];

                return (
                  <React.Fragment key={user?.id}>
                    <tr className="hover:bg-muted/20 transition-colors border-t border-border/50">
                      <td className="pt-3 pb-1 px-2">
                        <div className="flex items-center gap-2">
                          {rankIcon ? (
                            <Icon name={rankIcon.name} size={20} color={rankIcon.color} />
                          ) : (
                            <span className="text-sm md:text-base font-bold text-foreground w-5 text-center">
                              {user?.rank}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="pt-3 pb-1 px-2">
                        <div className="flex items-center gap-3">
                          <Image
                            src="/assets/images/no_image.png"
                            alt={user?.avatarAlt || "User avatar"}
                            className="w-8 h-8 md:w-10 md:h-10 rounded-full object-cover"
                          />
                          <div className="min-w-0">
                            <p className="font-semibold text-foreground text-sm md:text-base truncate">
                              {user?.name}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="pt-3 pb-1 px-2 text-right">
                        <span className="text-base md:text-lg font-bold text-primary data-text">
                          {user?.totalPoints}
                        </span>
                      </td>
                      <td className="pt-3 pb-1 px-2 text-right hidden md:table-cell">
                        <span className="text-sm md:text-base text-foreground data-text">
                          {user?.correctPredictions}/{user?.totalPredictions}
                        </span>
                      </td>
                      <td className="pt-3 pb-1 px-2 text-right hidden lg:table-cell">
                        <span className="text-sm md:text-base text-foreground data-text">
                          {user?.accuracy}%
                        </span>
                      </td>
                    </tr>

                    {/* Inline category chips row */}
                    {hasAnyCategories && (
                      <tr className="hover:bg-muted/20 transition-colors">
                        <td className="pb-3 pt-0 px-2"></td>
                        <td colSpan="4" className="pb-3 pt-0 px-2">
                          {loadingBreakdowns ? (
                            <div className="flex flex-wrap gap-1.5 mt-1 pl-11">
                              {[...Array(4)].map((_, i) => (
                                <div
                                  key={i}
                                  className="h-5 w-20 rounded-full bg-muted/50 animate-pulse"
                                />
                              ))}
                            </div>
                          ) : (
                            <div className="mt-1 pl-11 space-y-1.5">
                              {/* Match results row */}
                              {(enabledCategories.length > 0 || earlyPredictionEnabled || confidenceBonusEnabled) && (
                                <div className="flex flex-wrap gap-1.5">
                                  {enabledCategories.map(cat => (
                                    <span
                                      key={cat.key}
                                      className="inline-flex items-center gap-1 bg-muted/40 rounded-full px-2.5 py-0.5 text-xs"
                                    >
                                      <span className="text-muted-foreground">{cat.label}</span>
                                      <span className="font-semibold text-foreground">
                                        {breakdown?.[cat.key] ?? 0}
                                      </span>
                                    </span>
                                  ))}
                                  {earlyPredictionEnabled && (
                                    <span className="inline-flex items-center gap-1 bg-muted/40 rounded-full px-2.5 py-0.5 text-xs">
                                      <span className="text-muted-foreground">Bonus</span>
                                      <span className="font-semibold text-foreground">
                                        {user?.pointsBreakdown?.bonusPoints ?? 0}
                                      </span>
                                    </span>
                                  )}
                                  {confidenceBonusEnabled && (
                                    <span className="inline-flex items-center gap-1 bg-muted/40 rounded-full px-2.5 py-0.5 text-xs">
                                      <span className="text-muted-foreground">Conf. Bonus</span>
                                      <span className="font-semibold text-foreground">
                                        {breakdown?.confidenceBonus ?? user?.pointsBreakdown?.confidenceBonus ?? 0}
                                      </span>
                                    </span>
                                  )}
                                </div>
                              )}
                              {/* Tournament results row */}
                              {tournamentResultCategories.length > 0 && (
                                <div className="flex flex-wrap gap-1.5">
                                  {tournamentResultCategories.map(cat => (
                                    <span
                                      key={cat.key}
                                      className="inline-flex items-center gap-1 bg-primary/10 rounded-full px-2.5 py-0.5 text-xs"
                                    >
                                      <span className="text-primary/70">{cat.label}</span>
                                      <span className="font-semibold text-primary">
                                        {breakdown?.[cat.key] ?? 0}
                                      </span>
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default LeaderboardTab;